// src/utils/okobaudatProperties.js
// Utility helpers for extracting and presenting OEKOBAUDAT material properties.
// The OEKOBAUDAT process JSON is not perfectly uniform across datasets, so these
// helpers lean on keyword-based heuristics and defensive parsing.

const PROPERTY_CATALOG = [
  {
    key: 'bulkDensity',
    label: 'Bulk density',
    keywords: [/bulk\s*density/i, /bulkdichte/i, /rohdichte/i]
  },
  {
    key: 'grossDensity',
    label: 'Gross density',
    keywords: [/gross\s*density/i, /bruttodichte/i]
  },
  {
    key: 'surfaceWeight',
    label: 'Surface weight',
    keywords: [/surface\s*weight/i, /fl\w*chengewicht/i]
  },
  {
    key: 'layerThickness',
    label: 'Layer thickness',
    keywords: [/layer\s*thickness/i, /schichtdicke/i, /dicke/i]
  },
  {
    key: 'linearDensity',
    label: 'Linear density',
    keywords: [/linear\s*density/i, /l\w*ngendichte/i]
  },
  {
    key: 'conversionFactor',
    label: 'Conversion factor',
    keywords: [/conversion\s*factor/i, /umrechnungsfaktor/i]
  },
  {
    key: 'biogenicCarbonContent',
    label: 'Biogenic carbon content',
    keywords: [/biogenic/i, /biogen/i]
  }
];

const PROPERTY_KEY_LOOKUP = PROPERTY_CATALOG.reduce((acc, entry) => {
  acc[entry.key.toLowerCase()] = entry.key;
  return acc;
}, {});

// Sentinel values used by OEKOBAUDAT for "not determined" biogenic carbon.
const BIOGENIC_SENTINELS = new Set([-1000, -9999]);

function sanitizeKey(label) {
  if (!label || typeof label !== 'string') return null;
  const normalized = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || null;
}

function getCanonicalLabel(canonicalKey, fallbackLabel, propertyKey) {
  if (canonicalKey) {
    const catalogEntry = PROPERTY_CATALOG.find((item) => item.key === canonicalKey);
    if (catalogEntry) {
      return catalogEntry.label;
    }
    return canonicalKey;
  }
  if (typeof fallbackLabel === 'string' && fallbackLabel.trim().length > 0) {
    return fallbackLabel.trim();
  }
  if (typeof propertyKey === 'string' && propertyKey.trim().length > 0) {
    return propertyKey;
  }
  return 'Property';
}

function ensureArray(node) {
  if (node == null) return [];
  if (Array.isArray(node)) return node;
  return [node];
}

function collectCandidateStrings(value) {
  const buffer = [];

  function visit(node) {
    if (node == null) return;
    if (typeof node === 'string' || typeof node === 'number') {
      const trimmed = String(node).trim();
      if (trimmed) buffer.push(trimmed);
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node === 'object') {
      if (node.value != null) visit(node.value);
      if (node.baseName != null) visit(node.baseName);
      if (node['@value'] != null) visit(node['@value']);
      if (node.name != null) visit(node.name);
      if (node.shortName != null) visit(node.shortName);
      if (node.text != null) visit(node.text);
      if (node.label != null) visit(node.label);
      if (node.title != null) visit(node.title);
      if (node.code != null) visit(node.code);
    }
  }

  visit(value);
  return buffer;
}

function extractNumeric(node) {
  if (node == null) return null;
  if (typeof node === 'number') return node;
  if (typeof node === 'string') {
    const trimmed = node.trim();
    if (!trimmed) return null;
    const number = Number(trimmed.replace(',', '.'));
    return Number.isFinite(number) ? number : null;
  }
  if (typeof node === 'object') {
    const numericKeys = [
      'meanValue',
      'value',
      'amount',
      'quantity',
      'numericalValue',
      'deterministicValue',
      'lowerBound',
      'upperBound',
      'resultingAmount',
      'factor'
    ];
    for (const key of numericKeys) {
      if (node[key] != null) {
        const extracted = extractNumeric(node[key]);
        if (extracted != null) return extracted;
      }
    }
  }
  return null;
}

function normalizeUnitString(rawUnit) {
  if (!rawUnit) return null;
  let unit = rawUnit.trim();
  unit = unit.replace(/m\^3|m3/gi, 'm³').replace(/m\^2|m2/gi, 'm²');
  return unit || null;
}

function extractUnit(node) {
  if (node == null) return null;
  if (typeof node === 'string') {
    return normalizeUnitString(node);
  }
  if (typeof node === 'object') {
    if (typeof node.unitName === 'string') return normalizeUnitString(node.unitName);
    if (typeof node.unit === 'string') return normalizeUnitString(node.unit);
    if (typeof node.name === 'string') return normalizeUnitString(node.name);
    if (typeof node.shortName === 'string') return normalizeUnitString(node.shortName);
    if (typeof node.referenceToUnit === 'string') return normalizeUnitString(node.referenceToUnit);
    const nestedUnitKeys = ['unit', 'unitName', 'referenceUnit'];
    for (const key of nestedUnitKeys) {
      if (node[key] && typeof node[key] === 'object') {
        const nested = extractUnit(node[key]);
        if (nested) return nested;
      }
    }
  }
  return null;
}

function registerPropertyCandidate(properties, entry, options = {}) {
  if (!entry || typeof entry !== 'object') return;

  const {
    valueSource,
    unitSource,
    fallbackLabel,
    rawOverride,
    additionalLabels
  } = options;

  const labelCandidates = collectCandidateStrings([
    entry.property,
    entry.name,
    entry.label,
    entry.code,
    entry.description,
    entry.shortDescription,
    entry.referenceToFlowPropertyDataSet,
    entry.referenceToFlowProperty,
    entry.referenceToFlowPropertyDataSet?.shortDescription,
    ...(Array.isArray(additionalLabels) ? additionalLabels : []),
    fallbackLabel
  ]);

  const canonicalKey = identifyCanonicalKey(entry, labelCandidates);
  const primaryLabel = labelCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

  let propertyKey = canonicalKey;
  if (!propertyKey && primaryLabel) {
    propertyKey = sanitizeKey(primaryLabel);
  }
  if (!propertyKey && typeof fallbackLabel === 'string') {
    propertyKey = sanitizeKey(fallbackLabel);
  }
  if (!propertyKey) return;
  if (properties[propertyKey]) return;

  let valueCandidate;
  if (valueSource !== undefined) {
    valueCandidate = extractNumeric(valueSource);
  } else {
    valueCandidate = extractNumeric(entry);
  }

  let unitCandidate;
  if (unitSource !== undefined) {
    unitCandidate = extractUnit(unitSource);
  } else {
    unitCandidate = extractUnit(entry.unit || entry.unitName || entry.referenceUnit || entry);
  }

  if (valueCandidate == null && canonicalKey !== 'conversionFactor') {
    return;
  }

  const label = getCanonicalLabel(canonicalKey, fallbackLabel || primaryLabel, propertyKey);

  properties[propertyKey] = {
    key: propertyKey,
    value: valueCandidate,
    unit: unitCandidate,
    label,
    raw: toPlainObject(rawOverride ?? entry)
  };
}

function traverseForProperties(root, properties) {
  if (!root || typeof root !== 'object') return;

  const visited = new WeakSet();
  const queue = [root];

  while (queue.length > 0) {
    const node = queue.shift();
    if (!node || typeof node !== 'object') continue;
    if (visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node)) {
      node.forEach((item) => queue.push(item));
      continue;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (!value) return;

      if (/flowPropert(y|ies)/i.test(key) || key === 'properties') {
        ensureArray(value).forEach((entry) => {
          if (!entry || typeof entry !== 'object') return;
          const candidates = entry.flowProperty ? ensureArray(entry.flowProperty) : ensureArray(entry);
          candidates.forEach((item) => registerPropertyCandidate(properties, item));
        });
      }

      if (typeof value === 'object') {
        queue.push(value);
      }
    });
  }
}

function parseFlowOtherSections(flowJson, properties) {
  if (!flowJson || typeof flowJson !== 'object') return;
  const otherEntries = ensureArray(flowJson?.flowInformation?.dataSetInformation?.other?.anies);

  otherEntries.forEach((anyEntry) => {
    const propertyDetails = new Map();
    ensureArray(anyEntry?.Metadata?.PropertyDetails).forEach((detail) => {
      if (!detail || typeof detail !== 'object') return;
      const identifier = detail.id || detail.ID || detail.property || detail.code || detail.Property;
      if (!identifier) return;
      propertyDetails.set(String(identifier).toLowerCase(), detail);
    });

    const materialCollections = [
      anyEntry?.Material,
      anyEntry?.Product,
      anyEntry?.Flow,
      anyEntry?.Component,
      anyEntry?.Properties
    ];

    materialCollections.forEach((collection) => {
      ensureArray(collection).forEach((group) => {
        if (!group || typeof group !== 'object') return;
        const propertyDataGroups = [
          group?.BulkDetails?.PropertyData,
          group?.PropertyData,
          group?.Properties,
          group?.Details
        ];

        propertyDataGroups.forEach((list) => {
          ensureArray(list).forEach((propData) => {
            if (!propData || typeof propData !== 'object') return;
            const propertyId = propData.property || propData.id || propData.code || propData.key || propData.Property;
            const detail = propertyId ? propertyDetails.get(String(propertyId).toLowerCase()) : null;

            const detailName = detail?.Name?.value || detail?.Name || propData.name;
            const unitsFromDetail = detail?.Units?.Unit?.[0]?.Name || detail?.Units?.name || detail?.Units;

            const entry = {
              property: propertyId,
              name: detailName,
              label: detailName || propData.label,
              code: detail?.id || propertyId,
              description: detail?.Description || propData.description,
              shortDescription: detail?.Name,
              unit: unitsFromDetail || propData.unit
            };

            const valueSource = propData.Data ?? propData.value ?? propData.meanValue ?? propData.amount ?? propData.result;
            const unitSource = propData.Unit ?? propData.unit ?? unitsFromDetail;

            const additionalLabels = [];
            if (detail?.Name?.value) additionalLabels.push(detail.Name.value);
            if (typeof detail?.Name === 'string') additionalLabels.push(detail.Name);
            if (Array.isArray(detail?.Name)) {
              detail.Name.forEach((nameEntry) => {
                if (!nameEntry) return;
                if (typeof nameEntry === 'string') {
                  additionalLabels.push(nameEntry);
                } else if (nameEntry.value) {
                  additionalLabels.push(nameEntry.value);
                }
              });
            }

            registerPropertyCandidate(properties, entry, {
              valueSource,
              unitSource,
              fallbackLabel: detailName || propertyId,
              rawOverride: { propertyData: propData, metadata: detail },
              additionalLabels
            });
          });
        });
      });
    });
  });
}

function identifyCanonicalKey(entry, labelCandidates) {
  const directProperty = typeof entry.property === 'string' ? entry.property : null;
  if (directProperty) {
    const normalized = directProperty.toLowerCase();
    if (PROPERTY_KEY_LOOKUP[normalized]) {
      return PROPERTY_KEY_LOOKUP[normalized];
    }
  }

  const allCandidates = [directProperty, ...(labelCandidates || [])].filter(Boolean);
  for (const candidate of allCandidates) {
    const lower = candidate.toLowerCase();
    const fromDirectMatch = PROPERTY_KEY_LOOKUP[lower];
    if (fromDirectMatch) return fromDirectMatch;

    for (const catalogEntry of PROPERTY_CATALOG) {
      if (catalogEntry.key.toLowerCase() === lower) return catalogEntry.key;
      if (catalogEntry.keywords.some((regex) => regex.test(candidate))) {
        return catalogEntry.key;
      }
    }
  }

  return null;
}

function detectDeclaredUnit(processJson) {
  const quantitativeReference = processJson?.processInformation?.quantitativeReference;
  const declaredCandidates = [];

  const referenceFlows = ensureArray(quantitativeReference?.referenceToReferenceFlow);
  for (const reference of referenceFlows) {
    const amount = extractNumeric(reference);
    const unit = extractUnit(reference.unit || reference.unitName || reference.referenceUnit || reference);
    if (amount != null && unit) {
      return `${amount} ${unit}`;
    }
    if (unit) {
      declaredCandidates.push(unit);
    }
  }

  if (quantitativeReference?.referenceUnit) {
    declaredCandidates.push(...collectCandidateStrings(quantitativeReference.referenceUnit));
  }
  if (quantitativeReference?.referenceToReferenceUnit?.unitName) {
    declaredCandidates.push(...collectCandidateStrings(quantitativeReference.referenceToReferenceUnit.unitName));
  }
  if (quantitativeReference?.referenceFunction?.referenceUnit) {
    declaredCandidates.push(...collectCandidateStrings(quantitativeReference.referenceFunction.referenceUnit));
  }
  if (processJson?.declaredUnit) {
    declaredCandidates.push(...collectCandidateStrings(processJson.declaredUnit));
  }

  const declared = declaredCandidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);
  return declared || null;
}

function toPlainObject(raw) {
  try {
    return JSON.parse(JSON.stringify(raw));
  } catch (error) {
    return null;
  }
}

export function extractFlowProperties(processJson, flowJson) {
  const properties = {};

  traverseForProperties(processJson, properties);
  traverseForProperties(flowJson, properties);
  parseFlowOtherSections(flowJson, properties);

  return { properties, declaredUnit: detectDeclaredUnit(processJson || flowJson || {}) };
}

export function formatProperty(property) {
  if (!property) return '—';
  const { value, unit } = property;
  if (value == null || Number.isNaN(Number(value))) return '—';

  const numeric = Number(value);
  const abs = Math.abs(numeric);
  let maximumFractionDigits = 3;
  if (abs >= 1000) maximumFractionDigits = 0;
  else if (abs >= 10) maximumFractionDigits = 1;
  else if (abs >= 1) maximumFractionDigits = 2;
  else if (abs >= 0.01) maximumFractionDigits = 3;
  else maximumFractionDigits = 4;

  const formattedValue = numeric.toLocaleString(undefined, { maximumFractionDigits });
  return unit ? `${formattedValue} ${unit}` : `${formattedValue}`;
}

export function isBiogenicSentinel(value) {
  if (value == null) return false;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return false;
  return BIOGENIC_SENTINELS.has(numeric);
}
